import { Injectable } from '@angular/core';
import { RestfulEndpoint } from './restful-endpoint.service';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { PointImage, ProjectDetails } from '../models/project.model';
import moment from 'moment';
import { GStatic } from './GStatic';
import { FloorPointDataResponse, ReportDetailsResponse } from '../models/reports.model';
import { PhasesApi, ProgressesApi, ProgressLogsApi } from '../models/phases.model';
import { Projects } from '../models/projects.model';

export interface LastReportImage {
  project_id: string;
  project_name: string;
  last_update: string;
  last_image_url: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProjectsService {
  allProjectLoaded: boolean = false;
  private projectData = new BehaviorSubject({ project_id: '' });
  // For Cashing
  private lastFetched: string[] = [];
  private projectsSubject = new BehaviorSubject<Projects[]>([]);
  readonly projects$ = this.projectsSubject.asObservable();
  private _isReadySubject = new BehaviorSubject<boolean>(false);
  private _isLoadingSubject = new BehaviorSubject<boolean>(false);
  private _fetches = new BehaviorSubject<string>('');
  //#endregion // isReady Subject & Observable
  readonly fetches$ = this._fetches.asObservable();
  private _started = false;
  private _token = localStorage.getItem('access_token');

  constructor(private endPoint: RestfulEndpoint) {}

  private _projects: Projects[] = null;

  get projects(): Projects[] {
    return this._projects;
  }

  //#region    isReady: boolean; --  isReady Subject & Observable
  private _isReady: boolean;

  set isReady(val: boolean) {
    this._isReady = val;
    this._isReadySubject.next(val);
  }

  get isReady$(): Observable<boolean> {
    return this._isReadySubject.asObservable();
  }

  //#endregion // End

  //#region    isLoaded: boolean; --  isLoaded Subject & Observable
  private _isLoaded: boolean;

  get isLoaded(): boolean {
    return this._isLoaded;
  }

  set isLoaded(val: boolean) {
    this._isLoaded = val;
    this._isLoadingSubject.next(val);
  }

  get isLoading$(): Observable<boolean> {
    return this._isLoadingSubject.asObservable();
  }

  //#endregion // End

  async fetchProjects(force?: boolean): Promise<void> {
    if (this._projects && !force) {
      return;
    }
    const itemPerPage = 1000;
    const projectsResponse = await this.getAllProjects(1, itemPerPage).toPromise();
    const totalProject = projectsResponse.total_project;
    this._projects = projectsResponse.projects;
    this.lastFetched.push('Projects');
    this.projectsSubject.next(this._projects);
    this._fetches.next('Projects');

    if (totalProject > itemPerPage) {
      setTimeout(() => {
        const sd = Math.ceil(totalProject / itemPerPage);
        const arr = [];
        for (let i = 2; i < sd + 1; i++) {
          arr.push(this.getAllProjects(i, itemPerPage));
        }
        forkJoin(arr).subscribe(async (results) => {
          results.forEach((v: any) => this._projects.push(...v.projects));
          this._projects.sort(
            (b, a) =>
              moment(a.created_at, 'YYYY-MM-DD hh:mm:ss a').unix() -
              moment(b.created_at, 'YYYY-MM-DD hh:mm:ss a').unix(),
          );
          this.lastFetched.push('Projects');
          this._fetches.next('Projects');
          this.lastFetched.push('allProjectLoaded');
          this._fetches.next('allProjectLoaded');
          this.allProjectLoaded = true;

          this.projectsSubject.next(this._projects);
        });
      }, 1000);
    } else {
      this.allProjectLoaded = true;
    }
    this._started = true;
  }

  fetchAndGetAllLastReportImage(): Observable<LastReportImage[]> {
    return this.endPoint.post('get_all_last_report_image', {});
  }

  getAllProjects(page: number = 1, per_page: number = 50): Observable<any> {
    return this.endPoint.post('get_all_projects', { per_page, page });
  }

  getProjects(
    project_name: string,
    type: string,
    fragment: string,
    page: number = 1,
    per_page: number = 50,
  ): Observable<any> {
    if (type === 'my-projects') {
      if (!fragment) {
        return this.endPoint.post('get_projects', {
          per_page,
          page,
          project_name,
        });
      } else {
        return this.endPoint.post('get_projects', {
          per_page,
          page,
          folder_id: fragment,
          project_name,
        });
      }
    } else if (type === 'shared-with-me') {
      return this.endPoint.post('projects_share_with_me', {
        per_page,
        page,
        project_name,
      });
    } else if (type === 'all-projects') {
    }
  }

  getProjectDetails(id: any): Observable<{ project_details: ProjectDetails }> {
    return this.endPoint.post('get_project_details', { id });
  }

  deleteProject(id: any): Observable<any> {
    return this.endPoint.post('delete_project', { id });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////
  getReportDetails(id: string): Observable<ReportDetailsResponse> {
    return this.endPoint.post('get_report_details', { id });
  }

  getReportDefaultFloor(reportId: string): Observable<FloorPointDataResponse> {
    return this.endPoint.get(`reports/${reportId}/get-default-point-data`);
  }

  getProjectReports(project_id: any): Observable<any> {
    return this.endPoint.post('project_reports', { project_id });
  }

  getReportPoint(point_id: string, floor_id: string): Observable<any> {
    return this.endPoint.post('get_reports_point', { point_id, floor_id });
  }

  updateCategory(body: any): Observable<any> {
    return this.endPoint.post('update_category', body);
  }

  createNewFolder(body: any): Observable<any> {
    return this.endPoint.post('create_new_folder', body);
  }

  createNewCategory(body: any): Observable<any> {
    return this.endPoint.post('create_new_category', body);
  }

  getCategories(project_id: any): Observable<any> {
    return this.endPoint.post('list_categories', { project_id });
  }

  moveToCategory(body: any): Observable<any> {
    return this.endPoint.post('move_to_category', body);
  }

  getCategoryDetails(body: any): Observable<any> {
    return this.endPoint.post('category_details', body);
  }

  deleteCategory(body: { flag: any; category_id: any }): Observable<any> {
    return this.endPoint.post('category_delete', body);
  }

  // Create Project

  createNewProject(body: any): Observable<any> {
    return this.endPoint.post('create_new_project', body);
  }

  createNewFloor(body: any): Observable<any> {
    return this.endPoint.post('create_new_floor', body);
  }

  createNewpPint(body: any): Observable<any> {
    return this.endPoint.post('create_new_point', body);
  }

  createNewBulkPoints(body: any): Observable<any> {
    return this.endPoint.post('create_new_bulk_points', body);
  }

  updatePoint(body: any): Observable<any> {
    return this.endPoint.post('update_point', body);
  }

  setDefaultPoint(id: string, project_id: string): Observable<any> {
    return this.endPoint.post('set_default_view', { id, project_id });
  }

  unmergeReport(report_id: string, project_id: string): Observable<any> {
    return this.endPoint.post('unmerge_reports', { report_id, project_id });
  }

  deleteReport(id: string, project_id: string): Observable<any> {
    return this.endPoint.post('delete_report', { id, project_id });
  }

  renameReport(body: any): Observable<any> {
    return this.endPoint.post('update_report', body);
  }

  showHidePoint(body: any): Observable<any> {
    return this.endPoint.post('hide_show_image', body);
  }

  deletePoint(body: any): Observable<any> {
    return this.endPoint.post('delete_point', body);
  }

  getFloorDetails(body: any): Observable<any> {
    return this.endPoint.post('get_floor_details', body);
  }

  downloadFloorImage(body: any): Observable<any> {
    return this.endPoint.post('download_floor_image', body);
  }

  getFloorsList(body: any): Observable<any> {
    return this.endPoint.post('get_floors_list', body);
  }

  checkProjectsUpdated(body: any): Observable<any> {
    return this.endPoint.post('check_projects_updated', body);
  }

  dataURItoBlob(dataURI, fileName) {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([arrayBuffer], { type: mimeString });
    return new File([blob], fileName);
  }

  getPointImages(
    project_id: string,
    report_id: string = null,
  ): Observable<{ point_images: PointImage[] }> {
    const data: any = { project_id };
    if (report_id) {
      data.report_id = report_id;
    }
    return this.endPoint.post('get_point_images', data);
  }

  getBuildings(project_id?: string): Observable<any> {
    return this.endPoint.post('get_my_project_names_list', {
      project_id: project_id ?? null,
    });
  }

  getPointImagesByPointId(point_id: string): Observable<{ point_images: PointImage[] }> {
    return this.endPoint.post('get_images_by_point_id', { point_id });
  }

  getPointImagesByFloorId(report_id: string, floor_id: string): Observable<any> {
    return this.endPoint.get(`floors/${floor_id}/images`, null, `reports/${report_id}`);
  }

  getNavigationHotspots(report_id: string, floor_id: string): Observable<any> {
    return this.endPoint.get(
      `floors/${floor_id}/navigation-hotspots`,
      null,
      `reports/${report_id}`,
    );
  }

  getMatchingPoints(report_id: string, floor_id: string): Observable<any> {
    return this.endPoint.get(
      `floors/${floor_id}/path-matching-points`,
      null,
      `reports/${report_id}`,
    );
  }

  /*Get full path*/
  getFullPath(report_id: string, high_quality: boolean, floor_id: string): Observable<any> {
    if (high_quality) {
      return this.endPoint.get(
        `${report_id}/points-extracted-video?selected_points_only=1&floor_id=${floor_id}`,
        null,
        'reports',
      );
    } else {
      return this.endPoint.get(
        `${report_id}/points-extracted-video?floor_id=${floor_id}`,
        null,
        'reports',
      );
    }
  }

  // Assign project manager
  assignProjectManager(project_id: string, user_id: string): any {
    return this.endPoint.post('assign_project_manager', {
      project_id,
      user_id,
    });
  }

  deleteFloor(id: string, project_id: string): any {
    return this.endPoint.post('delete_floor', { id, project_id });
  }

  editFloor(body: { floor_name: string; floor_id: string; project_id: string }): any {
    return this.endPoint.post('update_floor', body);
  }

  cloneFloor(body: { floor_name: string; floor_id: string }): any {
    return this.endPoint.post('clone', body, null, 'floors');
  }

  reOrderFloorsList(body: any): any {
    return this.endPoint.put(`v2/floors/update-order`, body);
  }

  toggleFloorObservationCreation(floorId: string, isEnabled: boolean): Observable<any> {
    return this.endPoint.patch(`v2/floors/${floorId}/toggle-observation-creation`, {
      is_enabled: isEnabled,
    });
  }

  bulkToggleFloorObservationCreation(
    floors: { floor_id: string; is_enabled: boolean }[],
  ): Observable<any> {
    return this.endPoint.patch(`v2/floors/bulk-toggle-observation-creation`, { floors });
  }

  reOrderFloorPointsList(body: any): any {
    return this.endPoint.put(`v2/floors/points/update-order`, body);
  }

  /*Phases*/

  // Create new phase
  createNewPhase(body): Observable<PhasesApi> {
    return this.endPoint.post('create_phase', body);
  }

  getPhases(project_id): Observable<PhasesApi[]> {
    return this.endPoint.post('phases', { project_id, sort: 'oldest' });
  }

  getProgressLogs(body): Observable<ProgressLogsApi> {
    return this.endPoint.post('phases_logs', body);
  }

  getProgresses(phase_id): Observable<ProgressesApi[]> {
    return this.endPoint.post('progress_log', { phase_id });
  }

  getAllProgresses(project_id): Observable<ProgressesApi[]> {
    return this.endPoint.post('project_progress_log', { project_id });
  }

  createProgress(body: any): Observable<ProgressesApi> {
    return this.endPoint.post('create_progress', body);
  }

  /*Hashtags endpoints*/
  getHashTags(): Observable<any> {
    return this.endPoint.post('tags', {});
  }

  mergeReports(body: { project_id: string; reports: string[] }): Observable<any> {
    return this.endPoint.post('merge_reports', body);
  }

  /**
   * Get reports that have images for a specific point
   * @param pointId - ID of the point
   */
  getReportsWithImages(pointId: string): Observable<any> {
    return this.endPoint.get(`floors/point/${pointId}/reports-having-point-images`, null);
  }

  /**
   * Get reports that have images for a specific point (in a video report)
   * @param pointId - ID of the point
   */
  getReportsWithPointVideos(pointId: string): Observable<any> {
    return this.endPoint.get(`floors/point/${pointId}/reports-having-point-videos`, null);
  }
}
